import { nextTick, inject } from "vue";
import { defineStore } from "pinia";
import screenfull from "screenfull";
import { useMiraStore } from "./mira";
import { useNuxtApp } from "nuxt/app";
import { useSceneStore } from "./scene";
import { usePropertyStore } from "./property";
const debug = console.log;

export const useViewerStore = defineStore("viewer", {
  state: () => {
    return {
      viewer: null,
      isReady: false,
      isFullscreen: false,
      selectedSceneId: null,
      interruptSignal: null,
      landscape: 0,
      scenes: {},
      isPortrait: false,
      activeHotspot: null,
      marzipanoReady: false,
      isFirstScene: true,

      // autorotate: Marzipano.autorotate({ yawSpeed: 0.1, targetPitch: 0 }),
    };
  },
  getters: {
    currentScene() {
      return this.scenes[this.selectedSceneId];
    },
    autorotate() {
      const { $marzipano } = useNuxtApp();
      return $marzipano.autorotate({
        yawSpeed: 0.1,
        targetPitch: 0,
      });
    },
    // nextScene() {
    //   const propertyStore = usePropertyStore()
    //   const leftScenes = propertyStore.autoTourScenes.filter(
    //     (scene) => !this.getSceneById(scene.id)?.visited
    //   )
    //   debug(leftScenes, 'leftScenes')
    //   return leftScenes.length > 0 ? leftScenes[0] : null
    // },
    // functional getters
    getSceneById: (state) => (id) => {
      return state.scenes[id];
    },
  },
  actions: {
    resetCurrentScene() {
      this.viewer = null;
      this.isReady = false;
      this.selectedSceneId = null;
    },
    setLandscape(value) {
      this.landscape = value;
    },
    nextScene() {
      const sceneStore = useSceneStore();
      const leftScenes = sceneStore.getAutoTourScenes.map((scene) => {
        return { ...scene, visited: this.getSceneById(scene.id)?.visited };
      });
      // debug(leftScenes, 'leftScenes')
      let filterScenes = leftScenes?.filter((scene) => !scene.visited);
      // debug(filterScenes, 'filterScenes')
      return filterScenes.length > 0 ? filterScenes[0] : null;
    },
    setIsReady(state) {
      window.busSocket.sendEvent({ name: "INTERACTED", state });
      this.isReady = state;
      nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    addNewScene(id, data) {
      this.scenes[id] = { ...data.data };
      this.scenes[id]._scene = data.scene;
      this.scenes[id]._connections = data.connections;
      debug("added new scene", this.scenes);
    },
    async setCurrentScene(id, hotspot = null, isFirst = false, source) {
      try {
        debug("setting current scene", id, hotspot);
        const { $device } = useNuxtApp();
        const miraStore = useMiraStore();
        //reset mira messages on mobile.
        if (this.marzipanoReady && this.isPortrait) {
          miraStore.resetChat("click");
        }
        this.resetOldScene(hotspot);
        const previouslyVisited = this.getSceneById(id)?.visited;
        debug("previously visited", previouslyVisited);
        let promise = await this.setNewScene(id, hotspot);
        // miraStore.messages.push({ text: 'Scene changed to ' + promise.name, type: 'user' })

        // post scene switch actions
        if (promise.lockScreen) {
          this.viewer.stopMovement();
          this.viewer.setIdleMovement(Infinity);
          this.viewer.controls().disable();
        } else {
          this.viewer.controls().enable();
          if (this.isFirstScene) this.viewer.startMovement(this.autorotate);
          if (this.$router?.currentRoute.value.name == "embed-do") {
            console.log("5 sec rotate");
            this.viewer.setIdleMovement(5000, this.autorotate);
          } else {
            console.log("15 sec rotate");
            this.viewer.setIdleMovement(15000, this.autorotate);
          }
        }

        // mira post scene switch actions
        if (this.isReady && promise.sceneInfo && !previouslyVisited) {
          miraStore.options = [];
          miraStore.pushAndPlay(promise.sceneInfo);
        }
        // analytics
        // window.busSocket.sendEvent({
        //   name: "SCENE_CHANGE",
        //   target: id,
        //   sceneName: promise.name,
        //   hotspot: hotspot ? hotspot : null,
        //   source: source ? source : null,
        // });

        return promise;
      } catch (e) {
        debug("error setting scene", e);
      }
    },
    setNewScene(id, hotspot = null) {
      debug("setting new scene", id, hotspot);
      return new Promise((resolve, reject) => {
        const { $device } = useNuxtApp();
        let scene = this.getSceneById(id);
        debug("setting new scene", scene);
        if (this.selectedSceneId === id) return resolve(scene);
        if (scene) {
          let fov = scene.initialViewParameters.fov;
          if (this.isPortrait) {
            fov = (115 * Math.PI) / 180;
          }
          let cords = {
            yaw: scene.initialViewParameters.yaw,
            pitch: 0,
            fov: scene.initialViewParameters.fov != 0 ? fov : Math.PI / 2,
          };

          // if (this.$router.currentRoute.name == "embed-do") {
          //   if (window.screen.orientation.type.includes("landscape"))
          //     cords.fov = (90 * Math.PI) / 180;
          //   else cords.fov = (120 * Math.PI) / 180;
          // }
          if (hotspot) {
            debug("clicked from hotspot");
            cords.yaw = hotspot.target.position.yaw;
          }
          debug("setting new scene", scene.id, scene.initialViewParameters.fov);
          if (hotspot) cords.yaw = hotspot.target.position.yaw;

          scene._scene.lookTo(cords, { transitionDuration: 10 }, () => {
            // debug('complete lookto')
            scene._scene.switchTo({ transitionDuration: 1000 }, (e) => {
              scene._scene.hotspotContainer().show();
              this.selectedSceneId = id;
              scene.visited = true;
              debug("scene change complete:", this.currentScene.id);
              resolve(scene);
            });
            this.marzipanoReady = true;
          });
        } else {
          this.interruptSignal = { id, hotspot };
          debug("Scene not found");
          reject("scene not found");
        }
      });
    },
    resetOldScene(hotspot = null) {
      if (this.currentScene) {
        debug("resetting old scene", this.currentScene.id);
        // this.activeHotspot = null;
        this.currentScene._connections.forEach((c) => {
          this.activeHotspot = null;
          // c.component.component.data.active = false;
        });

        // TODO
        // pointer removal
        // lookto clicked hotspot (animatebeforeleave)
      }
    },
    changeFullscreen() {
      this.isFullscreen = screenfull.isFullscreen;
      nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    async toggleFullScreen() {
      debug("toggle fullscreen", screenfull.isEnabled, screenfull.isFullscreen);
      if (!screenfull.isFullscreen) {
        window.busSocket.sendEvent("FULLSCREEN");
        await screenfull.request(document.documentElement, {
          navigationUI: "hide",
        });
        screenfull.on("change", this.changeFullscreen);
        if (window.screen.orientation?.lock)
          window.screen.orientation.lock("landscape");
      } else {
        window.busSocket.sendEvent("EXIT_FULLSCREEN");
        await screenfull.exit();
      }
    },
  },
});
